import { GiftCard } from '@kinomap-dev/ng-ui-lib';
import { Coupon } from '../models/coupon.model';
import { KinoSubscription } from '../models/kino-subscription.model';
import { Order, OrderTypes } from '../models/order.model';
import { PlanTypes } from '../models/plan.model';
import { SubscriptionPlan } from 'app/models/subscrition-plan.model';

export const getOrderType = (order: Order): OrderTypes => {
  if (!!order?.planType) {
    return OrderTypes.Subscription;
  }
  if (!!order?.voucherDuration) {
    return OrderTypes.Voucher;
  }
  return OrderTypes.Family;
};

export const getOrderPlan = (
  order: Order | null,
  plans: SubscriptionPlan[]
): SubscriptionPlan | null => {
  if (!order || !order.planType) {
    return null;
  }
  const orderPlan: SubscriptionPlan | undefined = plans.find((plan) => {
    return plan.name === order.planType;
  });
  if (!orderPlan) {
    return null;
  }
  return orderPlan;
};

export const getOrderVoucherPlan = (
  order: Order | null,
  voucherPlans: readonly GiftCard[]
): GiftCard | null => {
  if (!order || !order.voucherDuration) {
    return null;
  }

  const orderVoucherPlan: GiftCard | undefined = voucherPlans.find(
    (voucherPlan) => {
      return voucherPlan.name === order.voucherDuration;
    }
  );

  if (!orderVoucherPlan) {
    return null;
  }
  return orderVoucherPlan;
};

export const getIsMigration = (
  order: Order | null,
  activeSubscription: KinoSubscription | null
): boolean => {
  return (
    order?.planType === PlanTypes.Year &&
    activeSubscription?.plan?.name === PlanTypes.Month
  );
};

export const getOrderPrice = ({
  order,
  activeSubscription,
  familyOptionPrice,
  plans,
  voucherPlans,
}: {
  order: Order | null;
  activeSubscription: KinoSubscription | null;
  familyOptionPrice: number;
  plans: SubscriptionPlan[];
  voucherPlans: readonly GiftCard[];
}): number => {
  const isOnlyFamily: boolean = !order.planType && !order.voucherDuration;

  if (isOnlyFamily) {
    return familyOptionPrice;
  }

  if (!!order?.voucherDuration) {
    const orderVoucherPlan: GiftCard | null = getOrderVoucherPlan(
      order,
      voucherPlans
    );

    return orderVoucherPlan?.price;
  }

  let priceToDeduce: number = 0;

  const orderPlan: SubscriptionPlan | null = getOrderPlan(order, plans);
  const isMigration: boolean = getIsMigration(order, activeSubscription);

  if (isMigration) {
    priceToDeduce = activeSubscription.plan.price;
    if (order.hasFamilyOption && !activeSubscription.familyOption) {
      priceToDeduce -= familyOptionPrice;
    }
  }

  return orderPlan?.price - priceToDeduce;
};

export const getPriceToPayNow = ({
  order,
  activeSubscription,
  familyOptionPrice,
  plans,
  voucherPlans,
}: {
  order: Order | null;
  activeSubscription: KinoSubscription | null;
  familyOptionPrice: number;
  plans: SubscriptionPlan[];
  voucherPlans: readonly GiftCard[];
}): number => {
  let orderPrice: number = getOrderPrice({
    order,
    activeSubscription,
    familyOptionPrice,
    plans,
    voucherPlans,
  });

  return orderPrice;
};

export const getFuturePrice = ({
  order,
  activeSubscription,
  familyOptionPrice,
  plans,
  voucherPlans,
}: {
  order: Order | null;
  activeSubscription: KinoSubscription | null;
  familyOptionPrice: number;
  plans: SubscriptionPlan[];
  voucherPlans: readonly GiftCard[];
  coupon: Coupon | null;
}): number => {
  const orderPlan: SubscriptionPlan | null = getOrderPlan(order, plans);
  if (!order?.planType || !getIsMigration(order, activeSubscription)) {
    return getOrderPrice({
      order,
      activeSubscription,
      familyOptionPrice,
      plans,
      voucherPlans,
    });
  }
  return orderPlan.price;
};

export const getFuturePriceDate = ({
  order,
  activeSubscription,
}: {
  order: Order | null;
  activeSubscription: KinoSubscription | null;
}): Date => {
  const isMigration = getIsMigration(order, activeSubscription);
  if (!isMigration) {
    return null;
  }
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date;
};

export const getOrderFamilyOptionDate = ({
  order,
  activeSubscription,
}: {
  order: Order | null;
  activeSubscription: KinoSubscription | null;
}): Date | null => {
  if (!order?.hasFamilyOption) {
    return null;
  }

  if (!!activeSubscription?.familyOption) {
    return activeSubscription.familyOption.endDate;
  }

  const oneMonthFromNow: Date = new Date();
  oneMonthFromNow.setUTCHours(0, 0, 0, 0);
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  return oneMonthFromNow;
};

export const getIsOrderAFamilyOptionCancellation = (order: Order) => {
  const orderType: OrderTypes = getOrderType(order);
  return orderType === OrderTypes.Family && !order.hasFamilyOption;
};

export function getNextPeriodPrice({
  order,
  plans,
}: {
  order: Order | null;
  plans: SubscriptionPlan[];
}): number {
  if (!order) return 0;

  const plan: SubscriptionPlan = plans.find(
    (p: SubscriptionPlan) => p.name === order.planType
  );

  if (!plan) return 0;

  let nextPeriodPrice: number = plan.price;
  if (
    order?.coupon &&
    ((order.coupon.duration_in_months > 12 && order.planType === 'yearly') ||
      order.planType === 'monthly' ||
      order.coupon.duration === 'forever') &&
    order.coupon.duration !== 'once'
  ) {
    if (order.coupon.amount_off) {
      nextPeriodPrice -= order.coupon.amount_off;
    } else if (order.coupon.percent_off) {
      nextPeriodPrice -= (nextPeriodPrice * order.coupon.percent_off) / 100;
    }

    nextPeriodPrice = nextPeriodPrice < 0 ? 0 : nextPeriodPrice;
  }

  return nextPeriodPrice;
}
